import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { GalleryImage } from "@/types/database";

/**
 * Fetch active gallery images for an event, optionally filtered by category.
 * Ordered by sort_order ascending.
 */
export function useGalleryImages(
  eventId: string | undefined,
  category?: GalleryImage["category"]
) {
  return useQuery<GalleryImage[]>({
    queryKey: ["gallery-images", eventId, category],
    queryFn: async () => {
      let query = supabase
        .from("gallery_images")
        .select("*")
        .eq("event_id", eventId!)
        .eq("active", true)
        .order("sort_order", { ascending: true });

      if (category) {
        query = query.eq("category", category);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as GalleryImage[];
    },
    enabled: !!eventId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Fetch ALL gallery images for admin (includes inactive).
 */
export function useAdminGalleryImages(eventId: string | undefined) {
  return useQuery<GalleryImage[]>({
    queryKey: ["admin-gallery", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gallery_images")
        .select("*")
        .eq("event_id", eventId!)
        .order("category", { ascending: true })
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return (data || []) as GalleryImage[];
    },
    enabled: !!eventId,
  });
}
